/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

/**
 * Strips every non-digit character from a string.
 *
 * @param value - The raw input value.
 * @returns The value containing only digit characters.
 */
export function onlyDigits(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Removes any character that is not a letter (including accented) or space.
 *
 * @param value - The raw input value.
 * @returns The value containing only letters and spaces.
 */
export function onlyLetters(value: string): string {
  return value.replace(/[^a-zA-ZÀ-ÿ\s]/g, '');
}

/**
 * Formats a numeric string as a Brazilian CPF (`000.000.000-00`).
 *
 * @param value - The raw CPF input (digits or partially masked).
 * @returns The masked CPF, truncated to 11 digits.
 */
export function maskCPF(value: string): string {
  const digits = onlyDigits(value).slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

/**
 * Formats a numeric string as a Brazilian CNPJ (`00.000.000/0000-00`).
 *
 * @param value - The raw CNPJ input (digits or partially masked).
 * @returns The masked CNPJ, truncated to 14 digits.
 */
export function maskCNPJ(value: string): string {
  const digits = onlyDigits(value).slice(0, 14);
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

/**
 * Masks a document number as CPF or CNPJ depending on the client type.
 *
 * @param value - The raw document input.
 * @param type - Whether the client is an individual (CPF) or company (CNPJ).
 * @returns The masked document string.
 */
export function maskDocument(value: string, type: 'individual' | 'company'): string {
  return type === 'company' ? maskCNPJ(value) : maskCPF(value);
}

/**
 * Formats a numeric string as a Brazilian phone number, supporting both
 * 10-digit landlines and 11-digit mobiles.
 *
 * @param value - The raw phone input (digits or partially masked).
 * @returns The masked phone string, truncated to 11 digits.
 */
export function maskPhone(value: string): string {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d{1,4})$/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d{1,4})$/, '$1-$2');
}

/**
 * Formats a numeric string as a Brazilian date (`dd/mm/yyyy`).
 *
 * @param value - The raw date input (digits or partially masked).
 * @returns The masked date string, truncated to 8 digits.
 */
export function maskDate(value: string): string {
  const digits = onlyDigits(value).slice(0, 8);
  return digits
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{2})(\d)/, '$1/$2');
}

/**
 * Formats a numeric string as a grouped Brazilian currency amount (no symbol).
 *
 * @param value - The raw currency input (digits or partially masked).
 * @returns The grouped amount string, or empty when there are no digits.
 */
export function maskCurrency(value: string): string {
  const digits = onlyDigits(value).slice(0, 9);
  if (!digits) return '';
  const num = parseInt(digits, 10);
  return num.toLocaleString('pt-BR');
}

/**
 * Reverses {@link maskCurrency} back to a bare digit string.
 *
 * @param value - The masked currency input.
 * @returns The digits-only representation.
 */
export function unmaskCurrency(value: string): string {
  return onlyDigits(value);
}

/**
 * Formats a Brazilian RG (identity card) number.
 *
 * RG formats are issued per state and vary in length, so this only normalizes
 * the characters an RG can legitimately contain — digits plus a trailing check
 * character `X` — and applies the common `00.000.000-0` grouping when the value
 * is short enough for it. Longer state formats are left grouped by digits only,
 * rather than being forced into a layout they do not use.
 *
 * @param value - The raw RG input.
 * @returns The normalized RG, truncated to 14 characters.
 */
export function maskRG(value: string): string {
  const cleaned = value.toUpperCase().replace(/[^0-9X]/g, '').slice(0, 14);
  const digits = cleaned.replace(/X/g, '');
  const check = cleaned.endsWith('X') ? 'X' : '';
  if (digits.length + check.length > 9) return digits + check;
  const grouped = digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1})$/, '$1-$2');
  return check ? `${grouped}${grouped.includes('-') ? '' : '-'}${check}` : grouped;
}

/**
 * Formats an RG issuing agency (e.g. `SSP/SP`).
 *
 * Uppercases and keeps only letters and the state separator, so `ssp sp`
 * becomes `SSP/SP` as the user types.
 *
 * @param value - The raw issuing-agency input.
 * @returns The normalized agency string, truncated to 12 characters.
 */
export function maskIssuingAgency(value: string): string {
  const cleaned = value
    .toUpperCase()
    // Whitespace and hyphens read as the agency/UF separator, so `ssp sp`
    // becomes `SSP/SP` rather than collapsing into `SSPSP`.
    .replace(/[\s-]+/g, '/')
    .replace(/[^A-Z/]/g, '')
    .replace(/\/{2,}/g, '/')
    .slice(0, 12);
  const [agency, ...rest] = cleaned.split('/');
  const uf = rest.join('').slice(0, 2);
  return rest.length ? `${agency}/${uf}` : agency;
}

/**
 * Performs a lightweight structural check that a value looks like an email.
 *
 * @param value - The email candidate (empty is treated as valid/optional).
 * @returns Whether the value has an `@` and a dotted domain.
 */
export function isValidEmailFormat(value: string): boolean {
  if (!value) return true;
  const atIndex = value.indexOf('@');
  if (atIndex < 1) return false;
  const afterAt = value.slice(atIndex + 1);
  return afterAt.includes('.') && afterAt.indexOf('.') < afterAt.length - 1;
}
